import classnames from 'classnames';
import React, { forwardRef, useRef, useEffect, useContext } from 'react';
import styled, { ThemeContext, ThemeProvider } from 'styled-components';
import { InputLabelPositions, InputLooks } from '../../../../enums/input.js';
import '../../../../styles/styled-components/alert.js';
import { bounce } from '../../../../styles/styled-components/animations.js';
import '../../../../styles/styled-components/box-shadow.js';
import '../../../../styles/styled-components/button.js';
import '../../../../themes/alert.js';
import '../../../../themes/button.js';
import '../../../../themes/checkbox.js';
import '../../../../themes/divider.js';
import '../../../../themes/dropdown.js';
import '../../../../themes/fieldset.js';
import { fontFamilyBook } from '../../../../themes/fonts.js';
import '../../../../themes/input.js';
import '../../../../themes/list.js';
import '../../../../themes/listbox.js';
import '../../../../themes/loader.js';
import '../../../../themes/option.js';
import '../../../../themes/popout.js';
import '../../../../themes/switch.js';
import { lightTheme } from '../../../../themes/themes.js';
import '../../../../themes/tooltip.js';
import '../../../../styles/styled-components/font-face.js';
import '../../../../styles/styled-components/global.js';
import '../../../../styles/styled-components/input.js';
import '../../../../styles/styled-components/list.js';
import '../../../../styles/styled-components/listitem.js';
import '../../../../styles/styled-components/media-queries.js';
import '../../../../styles/styled-components/reset.js';
import '../../../../styles/styled-components/outline.js';
import '../../../../styles/styled-components/popout.js';
import { screenReaderOnly } from '../../../../styles/styled-components/screen-reader.js';
import '../../../../styles/styled-components/switch.js';
import '../../../../styles/styled-components/tooltip.js';
import '../../../../utilities/color.js';
import 'date-fns';
import '../../../../utilities/locale.js';
import { mergeRefs } from '../../../../utilities/refs.js';
import '../../../atoms/icon/font-icon/font-icon.js';
import 'react-transition-group';
import '../../../atoms/alert/inline-alert/inline-alert.js';
import '../../../atoms/alert/standard-alert/standard-alert.js';
import 'boring-avatars';
import '../../../atoms/tooltip/hover-tooltip/hover-tooltip.js';
import 'react-laag';
import '@brandfolder/utilities';
import '../../../atoms/button/close-button/close-button.js';
import '../../../atoms/button/color-button/color-button.js';
import '../../../../hooks/use-prevent-keyboard-scrolling.js';
import '../../../atoms/button/icon-button/icon-button.js';
import '../../../atoms/button/standard-button/standard-button.js';
import '../../../atoms/loader/circle-loader/circle-loader.js';
import '../../../atoms/loader/shimmer-loader/shimmer-loader.js';
import '../../../atoms/divider/standard-divider/standard-divider.js';
import '../../../atoms/fieldset/standard-fieldset/standard-fieldset.js';
import '../../../atoms/heading/standard-heading/standard-heading.js';
import { StandardLabel } from '../../../atoms/label/standard-label/standard-label.js';
import '../../../atoms/link/button-link/button-link.js';
import '../../../atoms/link/standard-link/standard-link.js';
import '../../../atoms/popout/standard-popout/standard-popout.js';
import '../../../atoms/screen-reader/only-for-screen-reader/only-for-screen-reader.js';
import '../../../atoms/table/standard-table/caption.js';
import '../../../atoms/table/standard-table/tbody.js';
import '../../../atoms/table/standard-table/tr-empty.js';
import '../../../atoms/table/standard-table/td.js';
import '../../../atoms/table/standard-table/tr.js';
import '../../../atoms/table/standard-table/table.js';
import '../../../atoms/table/standard-table/tfoot.js';
import '../../../atoms/table/standard-table/th.js';
import '../../../atoms/table/standard-table/thead.js';
import '../../../atoms/table/dnd-table/dnd-handle-td.js';
import '../../../atoms/table/dnd-table/dnd-table-drag-context.js';
import { HelpText } from '../../../atoms/text/help-text/help-text.js';
import '../../../atoms/text/standard-text/standard-text.js';
import '../../../internal/list/standard-list/standard-list.js';
import '../../../internal/listbox/standard-listbox/standard-listbox.js';
import '../../../internal/listitem/standard-listitem/standard-listitem.js';
import '../../../internal/option/standard-option/standard-option.js';
import '../../../internal/textarea/fake-textarea.js';
import '../../../internal/textfield/textfield-addon.js';
import '../../../internal/textfield/textfield-body.js';
import '../../../internal/textfield/textfield-header.js';
import '../../../internal/textfield/textfield-icon.js';
import '../../../internal/textfield/textfield-inner.js';
import { TextfieldWrapper } from '../../../internal/textfield/textfield-wrapper.js';
import { BaseCheckbox } from '../base-checkbox/base-checkbox.js';

const BaseStandardCheckbox = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { checkboxProps, className, defaultChecked, disabled, helpText, helpTextProps, id, indeterminate, inputProps, label, labelPosition, labelProps, look, name, onBlur, onChange, required, requiredLabel, showLabel, textProps, value, wrapperRef, ...otherProps } = props;
    const hasHelpText = !!helpText;
    const hasRequired = required && !!requiredLabel;
    const helpTextId = `${id}-help-text`;
    const labelId = `${id}-label`;
    const inputRef = useRef(null);
    useEffect(()=>{
        if (inputRef.current) {
            inputRef.current.indeterminate = indeterminate || false;
        }
    }, [
        indeterminate,
        inputRef
    ]);
    return /*#__PURE__*/ React.createElement(TextfieldWrapper, {
        ...otherProps,
        className: classnames({
            'bf-checkbox__standard': true,
            'bf-checkbox__standard--disabled': disabled,
            'bf-checkbox__standard--help-text': hasHelpText,
            'bf-checkbox__standard--indeterminate': indeterminate,
            [`bf-checkbox__standard--label-${labelPosition}`]: showLabel,
            'bf-checkbox__standard--label-hidden': !showLabel,
            [`bf-checkbox__standard--look-${look}`]: look,
            'bf-checkbox__standard--required': hasRequired,
            [`${className}`]: !!className
        }),
        labelPosition: labelPosition,
        ref: wrapperRef
    }, /*#__PURE__*/ React.createElement(BaseCheckbox, {
        ...inputProps,
        "aria-describedby": hasHelpText ? helpTextId : undefined,
        "aria-disabled": disabled,
        className: classnames({
            'bf-checkbox__input': true,
            [`${inputProps?.className}`]: !!inputProps?.className
        }),
        defaultChecked: defaultChecked,
        disabled: disabled,
        id: id,
        name: name,
        onBlur: onBlur,
        onChange: onChange,
        ref: mergeRefs([
            ref,
            inputRef
        ]),
        required: hasRequired,
        value: value
    }), /*#__PURE__*/ React.createElement(StandardLabel, {
        ...labelProps,
        className: classnames({
            'bf-checkbox__label': true,
            [`${labelProps?.className}`]: !!labelProps?.className
        }),
        htmlFor: id,
        id: labelId,
        required: hasRequired,
        requiredLabel: requiredLabel
    }, /*#__PURE__*/ React.createElement("span", {
        ...checkboxProps,
        "aria-hidden": true,
        className: classnames({
            'bf-checkbox__checkbox': true,
            [`${checkboxProps?.className}`]: !!checkboxProps?.className
        })
    }, /*#__PURE__*/ React.createElement("svg", {
        className: "bf-checkbox__checkmark",
        viewBox: "0 0 24 24"
    }, /*#__PURE__*/ React.createElement("polyline", {
        points: "20,6 9,17 4,12",
        strokeMiterlimit: "10",
        strokeWidth: "2"
    })), /*#__PURE__*/ React.createElement("svg", {
        className: "bf-checkbox__indeterminate",
        height: "1",
        width: "8",
        viewBox: "0 0 8 1"
    }, /*#__PURE__*/ React.createElement("rect", {
        height: "1",
        width: "8"
    }))), /*#__PURE__*/ React.createElement("span", {
        ...textProps,
        className: classnames({
            'bf-checkbox__text': true,
            [`${textProps?.className}`]: !!textProps?.className
        })
    }, label)), hasHelpText && /*#__PURE__*/ React.createElement(HelpText, {
        ...helpTextProps,
        id: helpTextId
    }, helpText));
});
const Textfield = styled(BaseStandardCheckbox)`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  position: relative;

  .bf-checkbox__checkmark,
  .bf-checkbox__indeterminate {
    display: none;
  }

  .bf-checkbox__input {
    ${screenReaderOnly}

    + .bf-checkbox__label {
      .bf-checkbox__checkbox {
        background-color: ${({ look, theme })=>theme.checkbox[look].backgroundColor};
        border: 1px solid ${({ look, theme })=>theme.checkbox[look].borderColor};
        border-radius: 2px;
        cursor: pointer;
        flex-shrink: 0;
        height: 14px;
        margin-block-start: 3.5px;
        outline-color: transparent;
        transition:
          background-color 0.2s ease,
          border-color 0.2s ease,
          outline-color 0.2s ease;
        width: 14px;

        &:hover {
          border-color: ${({ look, theme })=>theme.checkbox[look].borderColorHover};
        }
      }
    }

    &:checked {
      + .bf-checkbox__label {
        .bf-checkbox__checkbox {
          animation: ${bounce} 0.2s ease;
          background-color: ${({ look, theme })=>theme.checkbox[look].backgroundColorChecked};
          border-color: ${({ look, theme })=>theme.checkbox[look].borderColorChecked};

          .bf-checkbox__checkmark {
            display: block;

            polyline {
              fill: none;
              stroke: ${({ look, theme })=>theme.checkbox[look].fillColorChecked};
            }
          }
        }
      }
    }

    &:focus-visible {
      + .bf-checkbox__label {
        .bf-checkbox__checkbox {
          outline-color: ${({ look, theme })=>theme.checkbox[look].outlineColorFocus};
          outline-offset: 2px;
          outline-style: solid;
          outline-width: 2px;
        }
      }
    }

    &:indeterminate {
      + .bf-checkbox__label {
        .bf-checkbox__checkbox {
          animation: ${bounce} 0.2s ease;
          background-color: ${({ look, theme })=>theme.checkbox[look].backgroundColorIndeterminate};
          border-color: ${({ look, theme })=>theme.checkbox[look].borderColorIndeterminate};

          .bf-checkbox__indeterminate {
            display: block;
            margin: 5.5px 0 0 2px;

            rect {
              fill: ${({ look, theme })=>theme.checkbox[look].fillColorIndeterminate};
            }
          }
        }
      }
    }

    &:disabled {
      + .bf-checkbox__label {
        .bf-checkbox__checkbox {
          animation: none;
          background-color: ${({ look, theme })=>theme.checkbox[look].backgroundColorDisabled};
          border-color: ${({ look, theme })=>theme.checkbox[look].borderColorDisabled};
          cursor: not-allowed;

          .bf-checkbox__checkmark {
            polyline {
              fill: none;
              stroke: ${({ look, theme })=>theme.checkbox[look].fillColorDisabled};
            }
          }

          .bf-checkbox__indeterminate {
            rect {
              fill: ${({ look, theme })=>theme.checkbox[look].fillColorDisabled};
            }
          }
        }
      }
    }
  }

  .bf-label__standard {
    font-family: ${fontFamilyBook};
    font-weight: 400;
  }

  &.bf-checkbox__standard--label-hidden {
    .bf-checkbox__input {
      + .bf-checkbox__label {
        .bf-checkbox__checkbox {
          position: static;
        }
      }
    }

    .bf-checkbox__text {
      ${screenReaderOnly}
    }

    .bf-label__standard {
      margin-left: 0;
    }

    .bf-text__help {
      ${screenReaderOnly}
      margin-left: 0;
    }
  }

  &.bf-textfield__wrapper--label-position-right {
    .bf-checkbox__text {
      margin-inline-start: 12px;
    }

    .bf-text__help {
      margin-inline-start: 26px;
    }
  }

  &.bf-textfield__wrapper--label-position-left {
    align-items: flex-end;

    .bf-checkbox__checkbox {
      order: 1;
    }

    .bf-checkbox__text {
      margin-inline-end: 12px;
    }

    .bf-text__help {
      margin-inline-end: 26px;
    }
  }
`;
/**
 * A `<input type="checkbox">` element that represents a boolean (on/off) state.
 * `id` and `label` are required for accessibility.
 *
 * `InputLabelPositions.Right` is the default label position.
 * Only `InputLabelPositions.Left` is also currently supported.
 *
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox
 *
 * @param props StandardCheckboxProps
 * @returns FunctionComponent<StandardCheckboxProps>
 */ const StandardCheckbox = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { labelPosition = InputLabelPositions.Right, look = InputLooks.Default, showLabel = true, ...otherProps } = props;
    const themeContext = useContext(ThemeContext);
    return /*#__PURE__*/ React.createElement(ThemeProvider, {
        theme: themeContext || lightTheme
    }, /*#__PURE__*/ React.createElement(Textfield, {
        ...otherProps,
        labelPosition: labelPosition,
        look: look,
        ref: ref,
        showLabel: showLabel
    }));
});

export { StandardCheckbox };
//# sourceMappingURL=standard-checkbox.js.map
